/**
 * Google Ad Manager Configuration
 * Ad unit paths and sizes based on DraftKings Tagging Architecture
 */

import type {
  GoogleAdsPlacement,
  GoogleAdsPlacementKey,
  GoogleAdsPlacements,
  TenantConfig,
} from '@/hooks/useTenant';

/**
 * Google Ad Manager configuration interface
 */
export interface GAMConfig {
  networkId: string;
  enabled: boolean;
}

/**
 * Extract GAM configuration from tenant
 */
export const getGAMConfigFromTenant = (tenant: TenantConfig | undefined): GAMConfig | null => {
  if (!tenant || !tenant.gamEnabled) {
    return null;
  }

  return {
    networkId: tenant.gamNetworkId || '',
    enabled: tenant.gamEnabled,
  };
};

/**
 * Size mapping definition for responsive ads
 * Based on DraftKings Tagging Architecture
 */
export interface ResponsiveSizeMapping {
  viewport: [number, number]; // [width, height] of viewport
  sizes: [number, number][] | []; // Ad sizes for this viewport, [] = no ads
}

/**
 * Derive div ID prefix from placement key
 * Based on DraftKings Tagging Architecture
 * The prefix is used to generate unique div IDs like: {prefix}-{index}
 */
export const deriveDivIdPrefix = (placement: GoogleAdsPlacement): string => {
  return placement.targeting?.s3;
};

/**
 * Get ad unit path for a specific placement
 */
export const getAdUnitPath = (
  placements: GoogleAdsPlacements,
  placement: GoogleAdsPlacementKey
): string => {
  return placements[placement].adUnitPath;
};

/**
 * Get div ID prefix for a specific placement
 */
export const getDivIdPrefix = (
  placements: GoogleAdsPlacements,
  placement: GoogleAdsPlacementKey
): string => {
  return placements[placement].divIdPrefix;
};

/**
 * Generate unique div ID for an ad slot
 */
export const generateAdDivId = (
  placements: GoogleAdsPlacements,
  placement: GoogleAdsPlacementKey,
  isMultipleForPosition: boolean = true,
  index: number = 0
): string => {
  const prefix = getDivIdPrefix(placements, placement);
  return isMultipleForPosition ? `${prefix}-${index}` : prefix;
};

/**
 * Log ad configuration (for debugging)
 */
export const logAdConfig = (gamConfig: GAMConfig | null, tenant?: TenantConfig): void => {
  console.log('[Ad Config]', {
    tenant: tenant ? { name: tenant.name, code: tenant.code } : 'Not loaded',
    networkId: gamConfig?.networkId || 'N/A',
    enabled: gamConfig?.enabled || false,
  });
};
